import { ref, computed, watch, onUnmounted, type Ref } from 'vue'
import { Position } from "@vue-flow/core";
import type { RunHistory, ServiceNodeData, RunCanvasNode, RunCanvasEdge, ServiceRunStatus } from "@/types";
import { isFinishedRunStatus } from "@/types";
import { getLayoutedElements } from "@/composables/dagre-layout";
import { storedCanvasPosition } from "@/composables/canvas-layout";
import { edgeStyleForStatus } from "@/composables/edge-colors";

// Re-exports kept for downstream consumers that already import these from the
// management composable.
export {
  ALL_RUN_STATUSES,
  ALLOWED_PAGE_SIZES,
  DEFAULT_PAGE_SIZE,
  DEFAULT_TIME_SHORTCUT,
} from "./useHistoryFilters"
export type { RunStatus, TimeShortcut } from "./useHistoryFilters"

/** A run service as far as frontier derivation cares — id, its own status, and its parents. */
interface FrontierService {
  serviceId: string
  status: ServiceRunStatus
  parentServiceIds: string[]
}

/**
 * The effective run status per service, keyed by id. The data model has no distinct "running" status —
 * a service is Pending until it settles — so we infer the execution frontier: a Pending service whose
 * parents have ALL settled is what's running now; a Pending service still waiting on a parent hasn't
 * started, so it stays Pending (drawn grey). Derived from the graph, so it holds for real runs, not
 * just the simulator. Shared by the canvas edges and the stacked view's hierarchy lines so both animate
 * the same frontier.
 */
export function effectiveRunStatuses(
  services: readonly FrontierService[]
): Map<string, ServiceRunStatus> {
  const rawStatus = new Map(services.map((s) => [s.serviceId, s.status] as const))
  const isSettled = (id: string) => isFinishedRunStatus(rawStatus.get(id))

  const effective = new Map<string, ServiceRunStatus>()
  for (const service of services) {
    if (service.status !== "Pending") {
      effective.set(service.serviceId, service.status)
      continue
    }
    const allParentsSettled = service.parentServiceIds.every((parentId) => isSettled(parentId))
    effective.set(service.serviceId, allParentsSettled ? "Running" : "Pending")
  }
  return effective
}

/**
 * Composable for managing workflow execution history derived state — run
 * selection and node/edge generation for the canvas. Filtering, sorting, and
 * paging all live server-side now (see {@link useHistoryFilters} and ADR-007);
 * this composable just consumes the page the server returns. Edge styling lives
 * in the shared {@link edgeStyleForStatus} (see @/composables/edge-colors).
 */
export function useHistoryManagement(runHistory: Ref<RunHistory[]>) {
  // Selected run for history mode
  const selectedRun = ref<RunHistory | null>(null)

  // A run has no live frames left once it settles, so the switch to grey structure would snap the
  // instant the last service finishes. This holds the outcome colours briefly after that transition so
  // the run reads as "just finished" before the lines fade to grey (fade is a CSS stroke transition on
  // the edge path). Time-based, because a finished run is stable — nothing recomputes it otherwise.
  const FINISH_HOLD_MS = 1500
  const finishHeld = ref(false)
  let finishHoldTimer: ReturnType<typeof setTimeout> | null = null

  const runLive = (run: RunHistory | null) =>
    !!run && run.services.some((s) => !isFinishedRunStatus(s.status))

  function clearHold() {
    if (finishHoldTimer) {
      clearTimeout(finishHoldTimer)
      finishHoldTimer = null
    }
    finishHeld.value = false
  }

  watch(
    () => runLive(selectedRun.value),
    (live, wasLive) => {
      clearHold()
      if (wasLive && !live) {
        // Just finished — keep colouring the edges through the hold, then release to grey.
        finishHeld.value = true
        finishHoldTimer = setTimeout(clearHold, FINISH_HOLD_MS)
      }
    }
  )

  // Switching to a different run cancels an in-flight hold — otherwise the leftover timer would keep
  // colouring the newly-selected (static) run's edges. The live→settled watch above only fires on the
  // boolean flipping, so a settled→settled switch during the hold wouldn't reset it on its own.
  watch(
    () => selectedRun.value?.id,
    () => clearHold()
  )

  onUnmounted(clearHold)

  /**
   * The current page of runs, as returned by the server.
   *
   * Filtering (statuses, time range, search, workflow), sorting, and paging are
   * now all applied server-side — see {@link HistoryFilters.serverFilterParams}
   * and ADR-007. This is a passthrough kept under the old name so the many
   * call sites that read it (selection, auto-select-first, node generation)
   * don't change. The server returns rows already in the requested sort order,
   * so `[0]` is still "first under the current sort".
   */
  const filteredRunHistory = computed(() => runHistory.value)

  function getRunById(runId: string): RunHistory | null {
    return runHistory.value.find((run) => run.id === runId) || null
  }

  function selectRun(run: RunHistory) {
    selectedRun.value = run
  }

  function selectRunFromUrl(run: RunHistory) {
    selectedRun.value = run
  }

  // Structural edges: source → target from parentServiceIds, nothing else. This is the
  // graph shape both the layout (which node sits where) and the styled canvas edges are
  // built from, so the DAG the dagre pass lays out is exactly the one drawn.
  function structuralEdges(runHistory: RunHistory): { source: string; target: string }[] {
    return runHistory.services.flatMap((service) =>
      service.parentServiceIds.map((parentId) => ({
        source: parentId,
        target: service.serviceId,
      }))
    )
  }

  function convertRunHistoryToNodes(runHistory: RunHistory): RunCanvasNode[] {
    const nodes = runHistory.services.map((service) => {
      // The service's own time on the job, not the run's — a run's wall clock covers every service at
      // once, so stamping it on each card said the same thing nine times.
      const durationSec = service.processingMs
        ? `${(service.processingMs / 1000).toFixed(1)}s`
        : null

      return {
        id: service.serviceId,
        type: 'service',
        // Position is assigned below — from the run's snapshotted layout when it has one, else by dagre.
        data: {
          label: service.serviceName || 'Unnamed Service',
          executionNote: durationSec ? `completed in ${durationSec}` : service.status,
          // Real model type from the run payload, so history nodes/cards show the same per-type icon
          // as the editor canvas (via serviceTypeDisplay). Empty for legacy runs → neutral fallback.
          serviceType: service.serviceType,
          canvasMode: 'history',
          run: service,
        } satisfies ServiceNodeData,
      }
    })

    // All-or-nothing: only honour the snapshotted arrangement when every service in the run has one.
    // A partial set (a run whose workflow has since gained a service, or a mix of placed and unplaced)
    // would draw half-arranged, so fall back to a clean dagre pass rather than mix the two.
    const everyServiceHasLayout = runHistory.services.every((s) => storedCanvasPosition(s) !== null)
    if (everyServiceHasLayout) {
      // Reads with the arrangement the workflow had when it ran — the payoff of persisting builder
      // layout. Handles are the top-bottom sides dagre's default TB pass would set, so edges attach the
      // same way; the canvas re-runs dagre itself if the user switches to LR (session-only).
      const layoutById = new Map(runHistory.services.map((s) => [s.serviceId, storedCanvasPosition(s)!]))
      return nodes.map((node) => ({
        ...node,
        position: layoutById.get(node.id)!,
        targetPosition: Position.Top,
        sourcePosition: Position.Bottom,
      }))
    }

    // Lay the nodes out as a DAG in the default top-bottom direction. The canvas can re-run this
    // for LR / fit on its own local copy; this gives every consumer (canvas and stacked view) a
    // correct default without each one having to know about dagre.
    return getLayoutedElements(nodes, structuralEdges(runHistory), "TB").nodes
  }

  const historyNodes = computed<RunCanvasNode[]>(() => {
    if (!selectedRun.value) return []
    return convertRunHistoryToNodes(selectedRun.value)
  })

  // Per-service effective status (raw status + inferred running frontier). Shared with the stacked view.
  const effectiveStatusByService = computed<Map<string, ServiceRunStatus>>(() =>
    selectedRun.value ? effectiveRunStatuses(selectedRun.value.services) : new Map()
  )

  // Should the lines carry status colour right now? True while the run is live and through the brief
  // post-finish hold, then false — which drops the lines to grey. Shared by both views so they release
  // together.
  const runColoringActive = computed(() => runLive(selectedRun.value) || finishHeld.value)

  const historyEdges = computed<RunCanvasEdge[]>(() => {
    if (!selectedRun.value) return []
    const effective = effectiveStatusByService.value
    return structuralEdges(selectedRun.value).map((edge) => ({
      id: `e${edge.source}->${edge.target}`,
      source: edge.source,
      target: edge.target,
      type: "animated",
      // Target-keyed: a line's colour/animation describes the child it points at, so the line into a
      // node reads as that node's state (running → blue, outcome → green/red).
      ...edgeStyleForStatus(effective.get(edge.target), runColoringActive.value),
    }))
  })

  const historyStats = computed(() => {
    const total = runHistory.value.length
    const successful = runHistory.value.filter(run => run.status === 'Succeeded').length
    const failed = runHistory.value.filter(run => run.status === 'Failed').length
    const running = runHistory.value.filter(run => run.status === 'Running').length

    return {
      total,
      successful,
      failed,
      running,
      successRate: total > 0 ? Math.round((successful / total) * 100) : 0
    }
  })

  const recentRuns = computed(() => {
    return [...runHistory.value]
      .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
      .slice(0, 5)
  })

  return {
    // Selection
    selectedRun,

    // Computed
    filteredRunHistory,
    historyNodes,
    historyEdges,
    historyStats,
    recentRuns,

    // Shared run-animation state (canvas + stacked view read the same frontier and hold)
    effectiveStatusByService,
    runColoringActive,

    // Methods
    getRunById,
    selectRun,
    selectRunFromUrl,
  }
}
